<template>
    <div class="knowledge-detail">
        <div v-if="showReadingTimer" class="timer-float" aria-hidden="true">
            {{ timerDisplay }}
        </div>

        <div v-if="article" class="detail-scroll">
            <div class="detail-bg">
                <section class="card header-card">
                    <div class="header-row">
                        <div class="header-date">
                            <van-icon name="calendar-o" class="date-icon" />
                            <span>{{ displayDate }}</span>
                        </div>
                        <span :class="['header-read-tag', effectiveRead ? 'read' : 'unread']">
                            <img class="tag-icon" :src="seeIcon" alt="" />
                            <span class="tag-text">{{ readLabel }}</span>
                        </span>
                    </div>
                    <h1 class="detail-title">{{ article.title }}</h1>
                </section>

                <section class="card body-card">
                    <div v-if="article.cover" class="body-cover-wrap">
                        <img class="body-cover" :src="article.cover" alt="" />
                    </div>
                    <div class="article-html" v-html="article.content"></div>
                    <div class="read-hint">
                        <div class="read-hint-line" />
                        <p class="read-hint-text">至少浏览 90S 以上才能被判断已阅读完成</p>
                    </div>
                </section>
            </div>
        </div>

        <div v-else class="detail-empty">
            <van-empty description="暂无文章" />
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { finishedReading, getTwInfo } from '@/api/index.js';

const READ_COMPLETE_SECONDS = 90;

export default {
    name: 'KnowledgeDetail',
    data() {
        return {
            seeIcon: require('@/assets/image/see-icon.png'),
            article: null,
            readSeconds: 0,
            timerId: null,
            readingFinishLoading: false,
            readingFinishedSent: false,
            loading: false
        };
    },
    computed: {
        displayDate() {
            if (!this.article) return '';
            const raw = this.article.createTime || this.article.publishTime || this.article.date || '';
            if (!raw) return '';
            const m = moment(raw);
            return m.isValid() ? m.format('YYYY年MM月DD日') : raw;
        },
        effectiveRead() {
            if (!this.article) return false;
            if (this.readingFinishedSent) return true;
            if (this.article.read) return true;
            if (typeof this.article.isRead === 'string' && this.article.isRead === '已阅读') {
                return true;
            }
            return false;
        },
        readLabel() {
            if (this.effectiveRead) return '已阅读';
            if (!this.article) return '未阅读';
            if (typeof this.article.isRead === 'string') {
                return this.article.isRead;
            }
            return '未阅读';
        },
        /** 未读且未完成上报时展示倒计时 */
        showReadingTimer() {
            return !!this.article && !this.readingFinishedSent && !this.effectiveRead;
        },
        /** 倒计时：距离满足阅读完成所需的剩余秒数 */
        timerDisplay() {
            const left = READ_COMPLETE_SECONDS - this.readSeconds;
            if (left > 0) {
                return `${left}s`;
            }
            return '完成';
        }
    },
    mounted() {
        this.loadDetail();
    },
    beforeDestroy() {
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    },
    methods: {
        getRouteId() {
            return this.$route.query.id || this.$route.params.id || '';
        },
        startReadTimer() {
            if (!this.showReadingTimer) return;
            if (this.timerId) {
                clearInterval(this.timerId);
                this.timerId = null;
            }
            this.timerId = setInterval(() => {
                this.readSeconds += 1;
                if (
                    this.readSeconds >= READ_COMPLETE_SECONDS &&
                    !this.readingFinishedSent &&
                    !this.readingFinishLoading
                ) {
                    this.trySubmitFinishedReading();
                }
            }, 1000);
        },
        loadDetail() {
            const id = this.getRouteId();
            if (!id) {
                this.$router.replace({ name: 'knowledge' });
                return;
            }
            this.loading = true;
            getTwInfo(id)
                .then(res => {
                    const data = res && res.data;
                    this.article = data || null;
                    if (!this.article) {
                        this.$router.replace({ name: 'knowledge' });
                        return;
                    }
                    this.readingFinishedSent =
                        this.article.read === true || this.article.isRead === '已阅读';
                    this.startReadTimer();
                })
                .catch(() => {
                    this.$router.replace({ name: 'knowledge' });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        trySubmitFinishedReading() {
            const id =
                this.article &&
                (this.article.id ?? this.article.knowledgeLearningId);
            if (id == null || id === '') {
                return;
            }
            this.readingFinishLoading = true;
            finishedReading(id)
                .then(() => {
                    this.readingFinishedSent = true;
                    this.readingFinishLoading = false;
                    if (this.timerId) {
                        clearInterval(this.timerId);
                        this.timerId = null;
                    }
                    if (this.article) {
                        this.article = {
                            ...this.article,
                            read: true,
                            isRead: '已阅读'
                        };
                    }
                })
                .catch(() => {
                    this.readingFinishLoading = false;
                });
        }
    }
};
</script>

<style scoped lang="scss">
.knowledge-detail {
    min-height: 100%;
    background: #eef4f5;
}

.detail-scroll {
    min-height: 100%;
    padding-bottom: 72px;
    box-sizing: border-box;
}

.detail-bg {
    padding: 12px 15px;
    background: linear-gradient(to bottom, #c2e5eb, #eef4f5);
}

.card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}

.header-card {
    padding: 16px;
    margin-bottom: 12px;
}

.header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.header-date {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #9c9c9c;
}

.date-icon {
    font-size: 16px;
    color: #9c9c9c;
}

.header-read-tag {
    display: inline-flex;
    align-items: center;
    height: 24px;
    border-radius: 4px;
    overflow: hidden;
    background-repeat: no-repeat;
    background-size: 100% 100%;
}

.header-read-tag.read {
    background-image: url('~@/assets/image/read-icon.png');
}

.header-read-tag.unread {
    background-image: url('~@/assets/image/unread-icon.png');
}

.header-read-tag .tag-icon {
    width: 24px;
    height: 24px;
    padding: 5px;
    box-sizing: border-box;
    object-fit: contain;
    flex-shrink: 0;
}

.header-read-tag .tag-text {
    padding: 0 8px;
    font-size: 12px;
    line-height: 24px;
    font-weight: 500;
}

.header-read-tag.read .tag-text {
    color: #46ccdc;
}

.header-read-tag.unread .tag-text {
    color: #ff7a00;
}

.detail-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #333;
    line-height: 1.45;
}

.timer-float {
    position: fixed;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 200;
    min-width: 44px;
    height: 44px;
    padding: 0 8px;
    box-sizing: border-box;
    border-radius: 22px;
    background: linear-gradient(135deg, #ff9a4a, #ff6633);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(255, 102, 51, 0.35);
    pointer-events: none;
}

.body-card {
    padding: 16px;
    position: relative;
    z-index: 1;
}

.body-cover-wrap {
    margin: -4px 0 16px;
    border-radius: 8px;
    overflow: hidden;
}

.body-cover {
    display: block;
    width: 100%;
}

.article-html {
    font-size: 15px;
    color: #333;
    line-height: 1.75;
    word-break: break-word;
}

.article-html ::v-deep p {
    margin: 0 0 12px;
}

.article-html ::v-deep ol,
.article-html ::v-deep ul {
    margin: 0 0 12px;
    padding-left: 1.4em;
}

.article-html ::v-deep li {
    margin-bottom: 8px;
}

.article-html ::v-deep strong {
    font-weight: 700;
}

.read-hint {
    margin-top: 28px;
    text-align: center;
}

.read-hint-line {
    height: 1px;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(0, 0, 0, 0.08) 20%,
        rgba(0, 0, 0, 0.08) 80%,
        transparent
    );
    margin-bottom: 12px;
}

.read-hint-text {
    margin: 0;
    font-size: 12px;
    color: #b0b0b0;
    line-height: 1.5;
}

.detail-empty {
    padding: 48px 20px;
}
</style>
